/**
 * The page's title bar: what this screen is, when the money last moved, and
 * the two operations an admin performs from it.
 *
 * "Run settlement" moves real money into seller balances, so it asks first and
 * shows what it is about to act on. It used to fire on the first click.
 */
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, PlayCircle, RotateCcw } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { Badge, Select } from '@terra/ui';

import { formatDateTime, formatPaise } from '../../lib/money';

export type PeriodDays = 7 | 30 | 90;

interface RunSettlementDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  pending: boolean;
  /** What the run is about to settle, straight from the overview. */
  eligible: { paise: number; count: number } | undefined;
  lastRunAt: string | null | undefined;
}

function RunSettlementDialog({
  open,
  onClose,
  onConfirm,
  pending,
  eligible,
  lastRunAt,
}: RunSettlementDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    confirmRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !pending) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, pending]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={() => !pending && onClose()}
          role="presentation"
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="run-settlement-title"
            className="bg-surface w-full max-w-md rounded-2xl p-6 shadow-xl"
            initial={{ opacity: 0, scale: 0.97, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 8 }}
            transition={{ duration: 0.18 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="run-settlement-title" className="text-ink text-[17px] font-bold">
              Run settlement now?
            </h2>
            <p className="text-ink-muted mt-1 text-[13px] font-medium leading-relaxed">
              This settles every order whose return window has closed, moving the money from the
              platform&apos;s liability to each seller&apos;s balance. The scheduled run does this
              at 07:30 IST on its own.
            </p>

            <dl className="border-border/60 bg-surface-subtle/40 mt-4 space-y-2 rounded-xl border p-4">
              <div className="flex items-baseline justify-between gap-4">
                <dt className="text-ink-muted text-[12.5px] font-semibold">Ready to settle</dt>
                <dd className="text-ink text-[15px] font-bold tabular-nums">
                  {eligible ? formatPaise(eligible.paise) : '—'}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-4">
                <dt className="text-ink-muted text-[12.5px] font-semibold">Orders</dt>
                <dd className="text-ink text-[13px] font-bold tabular-nums">
                  {eligible?.count ?? '—'}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-4">
                <dt className="text-ink-muted text-[12.5px] font-semibold">Last run</dt>
                <dd className="text-ink text-[13px] font-semibold">{formatDateTime(lastRunAt)}</dd>
              </div>
            </dl>

            {eligible?.count === 0 && (
              <p className="text-ink-muted mt-3 text-[12.5px] font-medium">
                Nothing is eligible right now. The run is safe to start and will settle nothing.
              </p>
            )}

            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={pending}
                className="border-border/60 bg-surface text-ink hover:bg-surface-subtle h-10 flex-1 rounded-lg border text-[13px] font-bold transition-colors disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                ref={confirmRef}
                type="button"
                onClick={onConfirm}
                disabled={pending}
                className="bg-ink hover:bg-ink/90 flex h-10 flex-1 items-center justify-center gap-2 rounded-lg text-[13px] font-bold text-white transition-colors disabled:opacity-60"
              >
                {pending && <Loader2 className="h-4 w-4 animate-spin" />}
                {pending ? 'Running…' : 'Run settlement'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

interface PayoutsHeaderProps {
  period: PeriodDays;
  onPeriodChange: (days: PeriodDays) => void;
  lastRunAt: string | null | undefined;
  eligible: { paise: number; count: number } | undefined;
  runDialogOpen: boolean;
  onOpenRunDialog: () => void;
  onCloseRunDialog: () => void;
  onRunSettlement: () => void;
  runPending: boolean;
  onReleaseReserves: () => void;
  releasePending: boolean;
}

export function PayoutsHeader({
  period,
  onPeriodChange,
  lastRunAt,
  eligible,
  runDialogOpen,
  onOpenRunDialog,
  onCloseRunDialog,
  onRunSettlement,
  runPending,
  onReleaseReserves,
  releasePending,
}: PayoutsHeaderProps) {
  return (
    <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
      <div>
        <h1 className="text-ink text-[26px] font-bold leading-tight sm:text-[28px]">Payouts</h1>
        <p className="text-ink-muted mt-1 text-[13.5px] font-medium">
          Seller settlement &amp; payment operations
        </p>
        <p className="text-ink-muted mt-2 flex items-center gap-2 text-[12.5px] font-semibold">
          <span
            className={`h-2 w-2 rounded-full ${lastRunAt ? 'bg-emerald-500' : 'bg-amber-500'}`}
            aria-hidden
          />
          {lastRunAt ? (
            <>Last settlement run: {formatDateTime(lastRunAt)}</>
          ) : (
            // Not a quiet day — the daily run has never completed.
            <span className="text-amber-700">No settlement run has ever completed</span>
          )}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2.5">
        <Select
          aria-label="Reporting period"
          value={String(period)}
          onValueChange={(v) => onPeriodChange(Number(v) as PeriodDays)}
          className="h-9 w-[150px] text-[12.5px]"
          options={[
            { value: '7', label: 'Last 7 days' },
            { value: '30', label: 'Last 30 days' },
            { value: '90', label: 'Last 90 days' },
          ]}
        />
        <button
          type="button"
          onClick={onReleaseReserves}
          disabled={releasePending}
          className="border-border/60 bg-surface text-ink hover:bg-surface-subtle flex h-9 items-center gap-1.5 rounded-lg border px-3.5 text-[12.5px] font-bold shadow-sm transition-colors disabled:opacity-60"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          {releasePending ? 'Releasing…' : 'Release matured reserves'}
        </button>
        <button
          type="button"
          onClick={onOpenRunDialog}
          className="bg-ink hover:bg-ink/90 flex h-9 items-center gap-1.5 rounded-lg px-3.5 text-[12.5px] font-bold text-white shadow-sm transition-colors"
        >
          <PlayCircle className="h-3.5 w-3.5" />
          Run settlement
        </button>
      </div>

      <RunSettlementDialog
        open={runDialogOpen}
        onClose={onCloseRunDialog}
        onConfirm={onRunSettlement}
        pending={runPending}
        eligible={eligible}
        lastRunAt={lastRunAt}
      />
    </header>
  );
}

/** Small helper the sections share for a "N items" chip. */
export function CountBadge({ count }: { count: number }) {
  return <Badge tone={count > 0 ? 'warning' : 'success'}>{count} pending</Badge>;
}
